/**
 * BandejaPanel — la cáscara común de las bandejas de la barra superior (Pedidos nuevos, @Menciones).
 *   · Celular (< 640 px): pantalla completa, con la cabecera pegada arriba, píldoras de filtro y áreas de toque de 48 px.
 *     Antes era un cuadro de 380 px anclado al botón y en el celular se salía de la pantalla por la izquierda.
 *   · Escritorio: desplegable anclado al botón, siempre dentro de la ventana.
 * Incluye también FilaBandeja (avatar grande, título fuerte si no se leyó, vista previa, hora, deslizar para marcar leído)
 * y agruparPorDia (Hoy · Ayer · Antes).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../lib/icons.jsx";
import { cn } from "../lib/utils.js";

const ANCHO = 400;

export function useEsMovil() {
  const [m, setM] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setM(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return m;
}

export function BandejaPanel({ abierto, onCerrar, anclaRef, panelRef, icono, titulo, subtitulo, acciones, pestanas, children }) {
  const movil = useEsMovil();
  const [pos, setPos] = useState(null);

  // Escritorio: se ancla al botón y se mantiene dentro de la ventana.
  useEffect(() => {
    if (!abierto || movil || !anclaRef?.current) return;
    const calc = () => {
      const r = anclaRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = Math.min(ANCHO, window.innerWidth - 16);
      setPos({ top: r.bottom + 8, left: Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8)), width: w });
    };
    calc();
    window.addEventListener("resize", calc);
    window.addEventListener("scroll", calc, true);
    return () => { window.removeEventListener("resize", calc); window.removeEventListener("scroll", calc, true); };
  }, [abierto, movil, anclaRef]);

  // Esc cierra; en el celular se bloquea el scroll de la página de atrás.
  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", onKey);
    const previo = document.body.style.overflow;
    if (movil) document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = previo; };
  }, [abierto, movil, onCerrar]);

  if (!abierto || (!movil && !pos)) return null;

  const cabecera = (
    <div className={cn("flex items-center gap-3 px-4 flex-shrink-0 bg-card", movil ? "pt-[max(12px,env(safe-area-inset-top))] pb-3" : "py-3 border-b border-border")}>
      {movil && (
        <button onClick={onCerrar} aria-label="Volver" className="h-11 w-11 -ml-2 rounded-full flex items-center justify-center text-foreground active:bg-muted">
          <Icon name="arrow_back" className="text-[24px]" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <h2 className={cn("font-extrabold tracking-tight leading-tight truncate", movil ? "text-[24px]" : "text-[17px]")}>{titulo}</h2>
        {subtitulo && <p className="text-[13px] text-muted-foreground truncate">{subtitulo}</p>}
      </div>
      {acciones}
      {!movil && (
        <button onClick={onCerrar} aria-label="Cerrar" className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted">
          <Icon name="close" className="text-[18px]" />
        </button>
      )}
    </div>
  );

  const cuerpo = (
    <>
      {cabecera}
      {pestanas && <div className="flex items-center gap-2 px-4 pb-3 flex-shrink-0 overflow-x-auto bg-card" role="tablist">{pestanas}</div>}
      <div className={cn("flex-1 overflow-y-auto overscroll-contain", movil && "pb-[max(16px,env(safe-area-inset-bottom))]")}>{children}</div>
    </>
  );

  if (movil) {
    return createPortal(
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={titulo} className="fixed inset-0 z-[9990] bg-background flex flex-col">
        {cuerpo}
      </div>,
      document.body
    );
  }
  return createPortal(
    <div ref={panelRef} role="dialog" aria-label={titulo}
         className="fixed z-50 max-h-[min(640px,calc(100vh-96px))] bg-card border border-border rounded-3xl shadow-2xl overflow-hidden flex flex-col"
         style={{ top: pos.top, left: pos.left, width: pos.width }}>
      {cuerpo}
    </div>,
    document.body
  );
}

/** Píldora de filtro (Sin leer · Todas). */
export function PildoraBandeja({ activa, onClick, children }) {
  return (
    <button role="tab" aria-selected={activa} onClick={onClick}
      className={cn("h-9 px-4 rounded-full text-[14px] font-extrabold whitespace-nowrap transition-colors flex-shrink-0",
        activa ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground")}>
      {children}
    </button>
  );
}

export function haceCuanto(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "Ahora";
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h`;
  const dias = Math.round(h / 24);
  if (dias < 7) return `${dias} d`;
  return d.toLocaleDateString("es-PY", { day: "2-digit", month: "short" });
}

/** Agrupa por día: Hoy · Ayer · Antes (lista ya ordenada de más nueva a más vieja). */
export function agruparPorDia(items) {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const grupos = [];
  const poner = (t, it) => { const g = grupos.find(x => x.t === t); if (g) g.items.push(it); else grupos.push({ t, items: [it] }); };
  for (const it of items) {
    const d = new Date(it.created_at); d.setHours(0, 0, 0, 0);
    const dif = Math.round((hoy - d) / 86400000);
    poner(dif <= 0 ? "Hoy" : dif === 1 ? "Ayer" : "Antes", it);
  }
  return grupos;
}

export function TituloGrupo({ children }) {
  return <div className="px-4 pt-4 pb-1 text-[13px] font-extrabold uppercase tracking-wider text-muted-foreground bg-background/60 sticky top-0 z-[1] backdrop-blur">{children}</div>;
}

/**
 * Fila de bandeja: avatar de 48 px (con punto si no se leyó), título fuerte si no se leyó, vista previa, hora.
 * Deslizar hacia la izquierda la marca como leída (con vibración corta).
 */
export function FilaBandeja({ icono, tono = "c", titulo, vista, hora, sinLeer, onAbrir, onLeida, destino }) {
  const x0 = useRef(null);
  const [dx, setDx] = useState(0);
  const TONOS = { c: ["#CFFAFE", "#155E75"], a: ["#FEF3C7", "#92400E"], v: ["#EDE9FE", "#5B21B6"], g: ["#D1FAE5", "#065F46"], r: ["#FEE2E2", "#B91C1C"] };
  const [fondo, color] = TONOS[tono] || TONOS.c;
  return (
    <div className="relative overflow-hidden border-b border-border/40">
      {/* El verde «Leído» solo existe mientras se desliza: antes estaba siempre detrás de la fila y, como el fondo de las
          no leídas era translúcido, se veía por debajo del texto y tapaba el título. */}
      {sinLeer && onLeida && dx < 0 && (
        <div className="absolute inset-y-0 right-0 w-28 bg-[#10B981] text-white flex items-center justify-center gap-1 text-[13px] font-extrabold" aria-hidden="true">
          <Icon name="check" className="text-[20px]" />Leído
        </div>
      )}
      <button
        onClick={onAbrir}
        onTouchStart={(e) => { x0.current = e.touches[0].clientX; }}
        onTouchMove={(e) => { if (x0.current != null && sinLeer && onLeida) setDx(Math.min(0, Math.max(-112, e.touches[0].clientX - x0.current))); }}
        onTouchEnd={() => { if (dx <= -80 && onLeida) { try { navigator.vibrate?.(10); } catch { /* sin vibración */ } onLeida(); } setDx(0); x0.current = null; }}
        style={{ transform: `translateX(${dx}px)`, transition: dx === 0 ? "transform .2s" : "none" }}
        className="relative w-full text-left px-4 py-3 min-h-[72px] flex items-center gap-3 bg-card active:bg-muted/60 overflow-hidden"
      >
        {sinLeer && <span className="absolute inset-0 bg-[#CFFAFE]/25 pointer-events-none" aria-hidden="true" />}
        <span className="relative flex-shrink-0">
          <span className="h-12 w-12 rounded-full flex items-center justify-center" style={{ background: fondo, color }}>
            <Icon name={icono} className="text-[24px]" />
          </span>
          {sinLeer && <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-[#0E7490] ring-2 ring-card" />}
        </span>
        <span className="relative flex-1 min-w-0">
          <span className="flex items-baseline gap-2">
            <span className={cn("flex-1 min-w-0 truncate text-[16px] leading-tight", sinLeer ? "font-extrabold text-foreground" : "font-semibold text-foreground/80")}>{titulo}</span>
            <span className={cn("text-[13px] flex-shrink-0 tabular-nums", sinLeer ? "font-extrabold text-[#0E7490]" : "text-muted-foreground")}>{hora}</span>
          </span>
          {vista ? <span className={cn("block text-[14px] leading-snug line-clamp-2 mt-0.5", sinLeer ? "text-foreground/80 pr-10" : "text-muted-foreground")}>{vista}</span> : null}
          {destino ? <span className="mt-1 flex items-center gap-1 text-[13px] font-extrabold text-[#0E7490]">{destino}<Icon name="arrow_forward" className="text-[15px]" /></span> : null}
        </span>
      </button>
      {/* «Marcar como leída» sin abrir la tarea: un toque (celular) o clic (escritorio). */}
      {sinLeer && onLeida && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onLeida(); }}
          aria-label="Marcar como leída"
          title="Marcar como leída"
          style={{ transform: `translateX(${dx}px)` }}
          className="absolute right-3 bottom-2 h-9 w-9 rounded-full bg-card ring-1 ring-border text-[#0E7490] flex items-center justify-center active:bg-[#CFFAFE]"
        >
          <Icon name="check" className="text-[20px]" />
        </button>
      )}
    </div>
  );
}

export function EsqueletoBandeja() {
  return (
    <div className="px-4 py-3 space-y-4" aria-hidden="true">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="flex items-center gap-3 animate-pulse">
          <span className="h-12 w-12 rounded-full bg-muted flex-shrink-0" />
          <span className="flex-1 space-y-2"><span className="block h-4 w-2/3 rounded bg-muted" /><span className="block h-3.5 w-full rounded bg-muted/70" /></span>
        </div>
      ))}
    </div>
  );
}

export function VacioBandeja({ icono, titulo, detalle }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-8 py-16">
      <span className="h-20 w-20 rounded-full bg-[#CFFAFE] text-[#155E75] flex items-center justify-center mb-4"><Icon name={icono} className="text-[40px]" /></span>
      <p className="text-[18px] font-extrabold">{titulo}</p>
      <p className="text-[15px] text-muted-foreground mt-1 max-w-[280px]">{detalle}</p>
    </div>
  );
}
